import React, { useState } from 'react';
import { toast } from 'sonner';

/**
 * TaskExecutionMeta — Round 34 (Round 78 hardened)
 *
 * Renders the tier-aware execution metadata that backend services
 * attach to every task (Growth Bot calendar days, Content Bot post
 * ideas, Run My Week days, Run My Business actions):
 *
 *   - "Where to use this" platform pill (primary + secondary)
 *   - Tier-correct asset card (Starter idea / Growth template /
 *     Pro ready-to-use copy) with a one-click Copy button when the
 *     backend marks the asset `copyable`.
 *
 * Round 78 (Feb 2026) — Trust Alignment defense-in-depth: every
 * field is coerced to a clean string before rendering. If an
 * upstream caller ever passes a raw post object (id /
 * publish_date / post_number / media_prompt / etc.) we render
 * empty instead of dumping JSON / curly braces into the UI.
 *
 * Pure presentational — no data fetching, no business logic.
 */

// Keys that should never appear in user-facing copy. If any of
// these are present in a string we treat the value as a leak and
// drop it.
const LEAK_KEYS = [
  'id', 'publish_date', 'publish_time', 'platform',
  'content_type', 'status', 'media_prompt', 'post_number',
  'scheduled_time', 'created_at', 'updated_at',
  'user_id', 'business_id',
];

const safeStr = (v, depth = 0) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') {
    if (v.indexOf('{') !== -1 && v.indexOf('}') !== -1 &&
        LEAK_KEYS.some((k) => v.indexOf(k) !== -1)) {
      return '';
    }
    return v;
  }
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) {
    if (depth > 2) return '';
    return v.slice(0, 5)
      .map((x) => safeStr(x, depth + 1))
      .filter(Boolean)
      .join(', ');
  }
  if (typeof v === 'object') {
    if (depth > 2) return '';
    const keys = ['title', 'name', 'text', 'hook', 'caption',
                  'topic', 'label', 'description'];
    for (const k of keys) {
      const candidate = v[k];
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }
    return '';
  }
  return '';
};

export const TaskExecutionMeta = ({
  action,
  whereToUse,
  tierAsset,
  testIdPrefix = 'task',
}) => {
  const [copied, setCopied] = useState(false);

  if (!whereToUse && !tierAsset) return null;

  // Coerce every render value through safeStr so a raw post dict
  // never reaches the DOM. Original objects are still passed to
  // logic (e.g., handleCopy) where they're handled safely.
  const safePrimary = safeStr(whereToUse?.primary_platform);
  const safeSecondary = safeStr(whereToUse?.secondary_platform);
  const safeLabel = safeStr(tierAsset?.label);
  const safeContent = safeStr(tierAsset?.content);
  const safeAction = safeStr(action);

  const handleCopy = async () => {
    if (!safeContent) return;
    try {
      await navigator.clipboard.writeText(safeContent);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy. Long-press the text to copy manually.');
    }
  };

  const tierBadge = {
    starter: 'bg-slate-200 text-slate-700',
    growth: 'bg-emerald-100 text-emerald-800',
    pro: 'bg-purple-100 text-purple-800',
  }[tierAsset?.tier || 'starter'];

  const tierLabel = {
    starter: 'Starter · Direction',
    growth: 'Growth · Template',
    pro: 'Pro · Done-for-you',
  }[tierAsset?.tier || 'starter'];

  return (
    <div
      className="mt-3 space-y-2 rounded-lg border border-slate-200 bg-white/60 p-3"
      data-testid={`${testIdPrefix}-execution-meta`}
    >
      {safeAction && (
        <p className="text-xs font-medium text-slate-500">{safeAction}</p>
      )}

      {/* Where to use this */}
      {whereToUse && safePrimary && (
        <div
          className="flex flex-wrap items-center gap-2 text-xs"
          data-testid={`${testIdPrefix}-where-to-use`}
        >
          <span className="text-slate-500">Where to use this:</span>
          <span
            className="rounded-full bg-blue-100 px-2 py-0.5 font-medium text-blue-800"
            data-testid={`${testIdPrefix}-primary-platform`}
          >
            {safePrimary}
          </span>
          {safeSecondary && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
              + {safeSecondary}
            </span>
          )}
        </div>
      )}

      {/* Tier-correct asset */}
      {tierAsset && safeContent && (
        <div className="rounded-md bg-slate-50 p-3">
          <div className="mb-1 flex items-center justify-between gap-2">
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${tierBadge}`}
              data-testid={`${testIdPrefix}-tier-badge`}
            >
              {tierLabel}
            </span>
            {tierAsset.copyable && (
              <button
                type="button"
                onClick={handleCopy}
                data-testid={`${testIdPrefix}-copy-btn`}
                className="rounded-md border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            )}
          </div>
          {safeLabel && (
            <p className="mb-1 text-[11px] font-semibold text-slate-700">
              {safeLabel}
            </p>
          )}
          <pre
            className="whitespace-pre-wrap break-words font-sans text-[12.5px] leading-relaxed text-slate-800"
            data-testid={`${testIdPrefix}-tier-content`}
          >
            {safeContent}
          </pre>
        </div>
      )}
    </div>
  );
};

export default TaskExecutionMeta;
